import { useState } from 'react';

import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import CloseIcon from '@/components/icons/close';
import LoadingIcon from '@/components/icons/loading';
import PhotoIcon from '@/components/icons/photo';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Dialog, DialogClose, DialogContent, DialogOverlay, DialogTitle } from '@/components/ui/dialog';

const LightboxImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setIsLoaded(true);
    setHasFailed(true);
  };

  return (
    <>
      {!isLoaded && (
        <div className="absolute flex items-center justify-center">
          <LoadingIcon className="text-default-blue size-12" />
        </div>
      )}
      {hasFailed ? (
        <div className="flex h-[48vh] flex-col items-center justify-center gap-2 text-white/70">
          <PhotoIcon className="size-16" />
          <span className="text-sm">Failed to load image</span>
        </div>
      ) : (
        <img
          src={src}
          className={`max-h-[85vh] max-w-full object-contain ${isLoaded ? 'block' : 'hidden'}`}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </>
  );
};

export const Lightbox = ({ photos, onHide, startIndex = 0 }: { photos: string[]; onHide: () => void; startIndex?: number }) => {
  return (
    <Dialog open={true} onOpenChange={onHide}>
      <DialogOverlay className="flex items-center justify-center bg-black/80">
        <DialogContent
          aria-describedby={undefined}
          aria-description="Full-screen photo carousel with navigation controls"
          className="relative top-0 max-h-[95vh] w-[95vw] max-w-[1200px] gap-1 overflow-auto rounded border-none p-0">
          <VisuallyHidden>
            <DialogTitle hideCloseButton>Image viewer</DialogTitle>
          </VisuallyHidden>
          <DialogClose className="absolute right-0 z-50 flex h-6 w-6 flex-row items-center justify-center rounded bg-black/40 text-white">
            <CloseIcon size={24} />
            <span className="sr-only">Close</span>
          </DialogClose>
          <div className="h-full w-full overflow-auto bg-black/50 p-2">
            <Carousel opts={{ loop: true, startIndex }}>
              <CarouselContent>
                {photos.map((photo, index) => (
                  <CarouselItem key={index} className="flex h-full min-h-20 items-center justify-center">
                    <LightboxImage src={photo} alt={`Image ${index + 1}`} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {photos.length > 1 && (
                <>
                  <CarouselPrevious className="left-2" />
                  <CarouselNext className="right-2" />
                </>
              )}
            </Carousel>
          </div>
        </DialogContent>
      </DialogOverlay>
    </Dialog>
  );
};
